/**
 * SoundManager — Souffle Diplomatique.
 *
 * Gestionnaire audio léger, sans dépendance (pas de Howler : les besoins
 * sont simples et l'API Audio suffit). Caractéristiques :
 *
 * - Préchargement paresseux : chaque son est instancié à la première demande
 *   puis mis en pool (3 instances par son pour permettre les chevauchements).
 * - Déblocage autoplay : les navigateurs bloquent l'audio avant le premier
 *   geste utilisateur — on s'arme sur le premier pointerdown/keydown.
 * - Mute persistant (localStorage indisponible dans certains contextes →
 *   fallback mémoire) + volume global.
 *
 * Registre : les fichiers vivent dans /sounds/*.ogg (client/public/sounds).
 */

import { cardsData } from "@pet-wars/shared";

export type SoundName =
  | "pet_timide"
  | "pet_discret"
  | "pet_confiant"
  | "pet_lourd"
  | "pet_catastrophique"
  | "pet_squeaky"
  | "pet_bouche"
  | "carte_reveal"
  | "roulement_tambour"
  | "verdict_reussite"
  | "verdict_echec"
  | "gong_exposition"
  | "tampon"
  | "toux_genee"
  | "clochette_seance"
  | "papier"
  | "commit_clic"
  | "sabotage"
  | "siege_capitale"
  | "fanfare_victoire";

/** Volumes relatifs par son (1 = plein volume master). Les sons d'ambiance
 *  et de friction fréquente sont baissés pour ne pas fatiguer. */
const SOUND_GAIN: Partial<Record<SoundName, number>> = {
  commit_clic: 0.4,
  papier: 0.5,
  tampon: 0.7,
  toux_genee: 0.6,
  carte_reveal: 0.55,
  clochette_seance: 0.5,
  roulement_tambour: 0.8,
  siege_capitale: 0.7,
  sabotage: 0.65,
};

const POOL_SIZE = 3;

class SoundManager {
  private pools = new Map<SoundName, HTMLAudioElement[]>();
  private cursor = new Map<SoundName, number>();
  private unlocked = false;
  private mutedMem = false;
  private masterVolume = 0.8;
  /** Sons dont on veut pouvoir couper la lecture en cours (ex. roulement). */
  private active = new Map<SoundName, HTMLAudioElement>();

  constructor() {
    // Déblocage autoplay au premier geste (une seule fois)
    const unlock = () => {
      this.unlocked = true;
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
  }

  get muted(): boolean {
    try {
      const v = window.localStorage?.getItem("sd_muted");
      if (v !== null && v !== undefined) return v === "1";
    } catch {
      /* stockage indisponible → fallback mémoire */
    }
    return this.mutedMem;
  }

  set muted(v: boolean) {
    this.mutedMem = v;
    try {
      window.localStorage?.setItem("sd_muted", v ? "1" : "0");
    } catch {
      /* fallback mémoire suffisant */
    }
    if (v) this.stopAll();
  }

  toggleMute(): boolean {
    this.muted = !this.muted;
    return this.muted;
  }

  /**
   * Joue un son. `opts.loop` pour les nappes (siège), `opts.volume` pour un
   * override ponctuel. Retourne l'élément joué (ou null si muet/bloqué).
   */
  play(
    name: SoundName,
    opts: { loop?: boolean; volume?: number; pitch?: number } = {}
  ): HTMLAudioElement | null {
    if (this.muted || !this.unlocked) return null;
    const pool = this.getPool(name);
    const idx = (this.cursor.get(name) ?? 0) % POOL_SIZE;
    this.cursor.set(name, idx + 1);
    const el = pool[idx]!;
    el.loop = Boolean(opts.loop);
    el.volume = Math.max(0, Math.min(1, (opts.volume ?? SOUND_GAIN[name] ?? 1) * this.masterVolume));
    // Pitch : par défaut les navigateurs COMPENSENT le changement de vitesse
    // pour préserver la hauteur — on désactive, sinon un pet "plus grave"
    // sonne exactement pareil, juste plus lent.
    const rate = Math.max(0.5, Math.min(2, opts.pitch ?? 1));
    const anyEl = el as HTMLAudioElement & {
      preservesPitch?: boolean;
      mozPreservesPitch?: boolean;
      webkitPreservesPitch?: boolean;
    };
    anyEl.preservesPitch = false;
    anyEl.mozPreservesPitch = false;
    anyEl.webkitPreservesPitch = false;
    el.playbackRate = rate;
    el.currentTime = 0;
    // play() peut rejeter si l'autoplay est bloqué malgré tout : silencieux.
    el.play().catch(() => {});
    this.active.set(name, el);
    return el;
  }

  /** Coupe une lecture en cours (roulement de tambour, nappe de siège...). */
  stop(name: SoundName): void {
    const el = this.active.get(name);
    if (el) {
      el.pause();
      el.currentTime = 0;
      this.active.delete(name);
    }
  }

  stopAll(): void {
    for (const name of this.active.keys()) this.stop(name);
  }

  private getPool(name: SoundName): HTMLAudioElement[] {
    let pool = this.pools.get(name);
    if (!pool) {
      pool = Array.from({ length: POOL_SIZE }, () => {
        const el = new Audio(`/sounds/${name}.ogg`);
        el.preload = "auto";
        return el;
      });
      this.pools.set(name, pool);
    }
    return pool;
  }
}

export const sounds = new SoundManager();

/**
 * ────────────────────────────────────────────────────────────────────────
 * CASTING SONORE DES CARTES
 * ────────────────────────────────────────────────────────────────────────
 * Chaque carte doit sonner comme ce qu'elle est. Trois niveaux :
 *
 *  1. Identité propre — une poignée de cartes a un son signature imposé
 *     (Le Pet Originel, Bruit de Pet avec la Bouche, Quinte de Toux…).
 *  2. Sinon, le son est déduit des STATS RÉELLES de la carte (densité ×
 *     étendue) et de ses mots-clés : une carte discrète (`silent`) chuchote,
 *     une grosse extension explose. C'est automatique — une nouvelle carte
 *     ajoutée dans cards.json est castée sans toucher au code.
 *  3. Variation de tonalité — chaque lecture applique un léger décalage de
 *     hauteur, déterministe par carte (deux Choucroutes se ressemblent) mais
 *     ré-aléatorisé à chaque jeu. Sans ça, entendre 4 fois la même extension
 *     dans un tour donnait l'impression d'un unique bruit en boucle.
 */

/** Sons signature imposés, par id RÉEL de carte (cf. shared/data/cards.json). */
const CARD_SOUND: Record<string, SoundName> = {
  pet_originel: "pet_catastrophique",     // D5 E7 — l'apocalypse
  bruit_pet_bouche: "pet_bouche",         // imitation buccale (un rot) = le gag
  tentative_discrete: "pet_timide",       // silent, D1 E1
  le_chaud_qui_suinte: "pet_discret",     // silent : ça ne s'entend pas, ça s'installe
  quinte_toux: "toux_genee",              // pas un pet : une toux
  pet_chasse: "pet_squeaky",              // D4 E1, "range" — sec et perçant
  choucroute_garnie: "pet_catastrophique",// D4 E2 — la grosse artillerie
  camembert_centenaire: "pet_lourd",      // toxic
  pet_panoramique: "pet_confiant",        // E5 — large et assumé
  saturation_regionale: "pet_confiant",   // E5
  reveillon_noel_famille: "pet_catastrophique", // D3 E3, famille réunie
  pet_retardement: "pet_lourd",           // delayed, inevitable
  aveu_spontane: "pet_confiant",          // on assume
  oups_cetait_pas_un_pet: "pet_squeaky",  // le couac
  verrou_securite: "pet_lourd",
  renferme_officiel: "pet_discret",
};

/** Stats des cartes, lues une fois depuis les données partagées. */
const CARD_STATS = new Map<string, { density: number; spread: number; silent: boolean }>();
for (const c of cardsData as any[]) {
  const st = c.stats ?? {};
  const kws: string[] = (c.keywords ?? []).map((k: any) =>
    typeof k === "string" ? k : String(k?.type ?? "")
  );
  CARD_STATS.set(String(c.id), {
    density: Number(st.density ?? 0),
    spread: Number(st.spread ?? 0),
    silent: kws.includes("silent"),
  });
}

/**
 * Choisit le pet approprié pour une carte : identité propre, sinon déduit
 * de la puissance réelle de la carte.
 */
export function fartForCard(cardId: string, category: string): SoundName {
  const forced = CARD_SOUND[cardId];
  if (forced) return forced;

  const st = CARD_STATS.get(cardId);
  if (st) {
    if (st.silent) return "pet_discret";
    const puissance = st.density * Math.max(1, st.spread);
    if (puissance >= 8) return "pet_catastrophique"; // D4E2, D3E3, D5E7…
    if (st.spread >= 3) return "pet_confiant";       // large étendue
    if (st.density >= 3) return "pet_lourd";         // concentré et dense
    if (puissance <= 1) return "pet_timide";         // D1 E1
    return "pet_confiant";
  }

  // Carte sans stats (risque/défense/sabotage pur) : au caractère de catégorie
  if (category === "risk") return "pet_squeaky";
  if (category === "density") return "pet_lourd";
  if (category === "extension") return "pet_confiant";
  return "pet_discret";
}

/**
 * Joue le pet d'une carte avec une variation de tonalité.
 * `seed` (l'instanceId par exemple) rend la variation stable pour une même
 * carte jouée, tout en restant différente d'une carte à l'autre.
 */
export function playFartForCard(cardId: string, category: string, seed?: string): void {
  const name = fartForCard(cardId, category);
  // Hash simple du cardId + seed → pitch reproductible dans un même contexte
  const key = `${cardId}|${seed ?? ""}`;
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  // ±12 % de hauteur : nettement audible sans dénaturer le son
  const pitch = 0.88 + ((h % 25) / 100);
  sounds.play(name, { pitch });
}

/**
 * Son joué au moment où une carte se révèle. Centralise la décision pour que
 * `reveal.ts` n'ait rien à arbitrer : identité propre de la carte > nature de
 * la catégorie. Les pets passent par `playFartForCard` (variation de hauteur).
 */
export function playCardRevealSound(
  card: { cardId: string; category: string; charged?: boolean; instanceId?: string }
): void {
  if (card.charged) {
    sounds.play("siege_capitale"); // quelque chose couve…
    return;
  }
  // Une carte à identité sonore imposée prime sur sa catégorie
  // (Quinte de Toux est un sabotage, mais doit tousser).
  const forced = CARD_SOUND[card.cardId];
  if (forced && !forced.startsWith("pet_")) {
    sounds.play(forced);
    return;
  }
  switch (card.category) {
    case "extension":
    case "density":
    case "signature":
    case "risk":
      playFartForCard(card.cardId, card.category, card.instanceId);
      return;
    case "sabotage":
      sounds.play("sabotage");
      return;
    case "defense":
      sounds.play("tampon");
      return;
    case "intel":
      sounds.play("papier");
      return;
  }
}
